import React from 'react';
import { Appearance, StripeConstructorOptions, StripeElementLocale } from '@stripe/stripe-js';
import { InferProps } from 'prop-types';

import {
  Processors,
  PaymentMethods,
  HTTP_METHOD_GET,
  HTTP_METHOD_POST,
  NEXT_ACTION_TYPE_REDIRECT,
  NEXT_ACTION_TYPE_STRIPE_CONFIRM,
  NEXT_ACTION_TYPE_DONE,
  NEXT_ACTION_TYPE_PAYPAL_ORDER,
  CaptureMode,
} from '../constants';
import {
  SlotPropType,
  PaymentElementPropTypes,
  PaymentElementThemePropTypes,
} from '../components/payment-element/payment.prop-types';
import enLabels from '../translations/langs/en';

import type { BankFormResponse } from '../components/payment-methods/Bank/bank.types';
import type { PayPalFormResponse } from '../components/payment-methods/PayPal/paypal.types';
import type { OxxoFormResponse } from '../components/payment-methods/Oxxo/oxxo.types';
import type { CreditCardFormResponse } from '../components/payment-methods/CreditCard/creditCard.types';

export type NextActionDetails = Record<string, any>;

type BaseNextAction = {
  type: string,
  onSuccess?: () => Promise<void>,
};

type NextActionDone = BaseNextAction & {
  type: typeof NEXT_ACTION_TYPE_DONE,
};

type NextActionRedirectGetDetails = {
  url: string,
  method?: typeof HTTP_METHOD_GET,
};

type NextActionRedirectGet = BaseNextAction & {
  type: typeof NEXT_ACTION_TYPE_REDIRECT,
  redirect: NextActionRedirectGetDetails,
  details?: NextActionRedirectGetDetails, // for backwards compatibility
};

type NextActionRedirectPostDetails = {
  url: string,
  method: typeof HTTP_METHOD_POST,
  postData?: Record<string, any>,
};

type NextActionRedirectPost = BaseNextAction & {
  type: typeof NEXT_ACTION_TYPE_REDIRECT,
  redirect: NextActionRedirectPostDetails,
  details?: NextActionRedirectPostDetails, // for backwards compatibility
};

type NextActionRedirect = NextActionRedirectGet | NextActionRedirectPost;

type NextActionStripeConfirmDetails = {
  url?: string, // for backwards compatibility
  return_url: string,
  client_secret: string,
};

type NextActionStripeConfirm = BaseNextAction & {
  type: typeof NEXT_ACTION_TYPE_STRIPE_CONFIRM,
  details?: NextActionStripeConfirmDetails, // for backwards compatibility
  stripe_confirmation_details: NextActionStripeConfirmDetails,
};

type NextActionPayPalOrderDetails = {
  order_id: string,
};
type NextActionPayPalOrder = BaseNextAction & {
  type: typeof NEXT_ACTION_TYPE_PAYPAL_ORDER,
  paypal_details: NextActionPayPalOrderDetails,
  details?: NextActionPayPalOrderDetails, // for backwards compatibility
};

export type NextAction = undefined
  | null
  | NextActionDone
  | NextActionRedirect
  | NextActionStripeConfirm
  | NextActionPayPalOrder;

export type PaymentMethodWithSubmitProps<T> = Omit<InferProps<T>, 'onSubmit'> & {
  onSubmit: (data: object) => Promise<NextAction>,
};

export type SlotProp = InferProps<typeof SlotPropType>;
export type PaymentElementThemeType = InferProps<typeof PaymentElementThemePropTypes>;
export type PaymentElementProps = PaymentMethodWithSubmitProps<typeof PaymentElementPropTypes>;
export type PaymentMethodsType = (typeof PaymentMethods)[number];

export interface StripeProcessor {
  apiVersion: string;
  account: string;
  clientKey: string;
}

export interface AdyenProcessor {
  [key: string]: any;
}

export interface PaypalProcessor {
  clientId: string;
}

export type ProcessorConfig = {
  processor: ProcessorType,
  stripe?: StripeProcessor,
  adyen?: AdyenProcessor,
  paypal?: PaypalProcessor,
}

export type ProcessorConfigs = ProcessorConfig[];

export type ProcessorType = (typeof Processors)[number];

export type PaymentMethodConfig = {
  processor: ProcessorType,
  tokenization?: Record<string, any>,
  constraints?: Record<string, any>,
};

export type PaymentMethodsConfig = {
  [key in PaymentMethodsType]?: PaymentMethodConfig;
};

export type PaymentGatewayOptions = StripeGatewayOptions;
export type ProcessorProps = {
  processors: ProcessorConfigs,
  method: PaymentMethodsType,
   // TODO: either type it properly or add additional field or ask to change v2/settings response
  methodConfig?: PaymentMethodConfig,
  billingConfig?: Record<string, any>,
  options?: Record<string, any>,
  children: React.ReactNode,
}

export interface StripeGatewayOptions {
  publishableKey?: string;
  appearance?: Appearance;
  configurationOptions?: StripeConstructorOptions;
  connectedAccount?: string;
  deferred?: boolean;
  betas?: boolean;
  locale?: StripeElementLocale;
  options?: Record<string, any>;
}

export interface Provider {
  createProvider: (params?: StripeGatewayOptions) => any;
}

export type LocaleType = string;

export type PaymentElementResponse = (
  CreditCardFormResponse | BankFormResponse | PayPalFormResponse | OxxoFormResponse
) & { paymentMethod: PaymentMethodsType, paymentMethodId?: any, paymentMethodHash?: any };

export type FormInputsType = Record<string, any>;

export type { ComponentStyleConfig } from '@chakra-ui/react';
export type { SystemStyleFunction } from '@chakra-ui/theme-tools';

export type LabelLangType = Record<keyof typeof enLabels, string>;
export type LabelsType = Record<LocaleType, LabelLangType>;
export type PaymentMethodSlotType = Record<PaymentMethodsType, React.ReactNode>;

export type CaptureModeType = typeof CaptureMode[keyof typeof CaptureMode];

export type IsBillingRequiredType = 'mandatory' | 'optional';
export type ModeType = 'live' | 'test';

/* eslint-disable @typescript-eslint/naming-convention */
export enum BillingFieldsEnum {
  'first_name',
  'last_name',
  'name',
  'birthday',
  'document',
  'document_number',
  'document_type',
  'tax_id',
  'address1',
  'address2',
  'city',
  'state',
  'country',
  'zip',
  'company',
  'email',
  'phone',
  'fax',
}
/* eslint-enable @typescript-eslint/naming-convention */

export type BillingSettingsResponseParams = {
  [key in keyof typeof BillingFieldsEnum]?: IsBillingRequiredType;
}

export interface SettingsResponseDataParams {
  gateway: typeof Processors[number];
  stripe: StripeProcessor;
  adyen: AdyenProcessor;
  billingAddressFields: BillingSettingsResponseParams;
  mode: ModeType;
  paymentMethods: typeof PaymentMethods[number][];
}

export interface Settings {
  billingAddressFields: BillingSettingsResponseParams;
  mode: ModeType;
  paymentMethods: (typeof PaymentMethods)[number];
  paymentMethodConfigurations: PaymentMethodsConfig;
  processors: ProcessorConfigs;
}

export interface SettingsResponseParams {
  success: boolean;
  statusMessage: string;
  data: SettingsResponseDataParams;
  statusCode: 0;
}
